import React from 'react';
import Icon from '../../../components/AppIcon';

const AgentCapacityOverview = ({ agentStatus, subscription, agentsConfig }) => {
  // Get subscription tier
  const tier = subscription?.subscription_plans?.tier || 'free';
  const tierName = tier === 'free' ? 'Free' : tier === 'draftiq_plus' ? 'DraftIQ+' : tier === 'enterprise' ? 'Enterprise' : 'Free';

  // Calculate max agents based on tier
  const getMaxAgents = () => {
    if (tier === 'enterprise') return 25;
    if (tier === 'draftiq_plus') return 4;
    return 2; // free
  };

  const maxAgents = getMaxAgents();

  // Calculate total active agents across all types
  const activeAgents = agentStatus?.agents?.filter(a => a?.isActive)?.length || 0;

  // Calculate remaining capacity
  const remainingCapacity = Math.max(0, maxAgents - activeAgents);

  // Calculate usage percentage
  const usagePercentage = maxAgents > 0 ? (activeAgents / maxAgents) * 100 : 0;

  // Determine progress bar color based on usage
  const getProgressColor = () => {
    if (usagePercentage >= 90) return 'bg-red-500';
    if (usagePercentage >= 70) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getProgressTextColor = () => {
    if (usagePercentage >= 90) return 'text-red-500';
    if (usagePercentage >= 70) return 'text-amber-500';
    return 'text-emerald-500';
  };

  // Get breakdown by agent type
  const getAgentTypeBreakdown = () => {
    return agentsConfig?.map(config => {
      const agentData = agentStatus?.agents?.find(a => a?.agentId === config?.dbId);
      return {
        name: config?.name,
        icon: config?.icon,
        color: config?.color,
        isActive: agentData?.isActive || false
      };
    });
  };

  const agentBreakdown = getAgentTypeBreakdown();

  // Get tier badge color
  const getTierBadgeColor = () => {
    if (tier === 'enterprise') return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
    if (tier === 'draftiq_plus') return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
    return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-2 font-heading">
            Agent Capacity Overview
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Current Subscription:</span>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getTierBadgeColor()}`}>
              {tierName}
            </span>
          </div>
        </div>
      </div>
      {/* Capacity Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Active Agents */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="Activity" size={18} className="text-primary" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Active Agents</span>
          </div>
          <p className="text-3xl font-bold text-foreground">{activeAgents}</p>
        </div>

        {/* Maximum Capacity */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="Target" size={18} className="text-primary" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Max Capacity</span>
          </div>
          <p className="text-3xl font-bold text-foreground">{maxAgents}</p>
        </div>

        {/* Remaining Capacity */}
        <div className="bg-muted/50 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Icon name="Zap" size={18} className={getProgressTextColor()} />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Remaining</span>
          </div>
          <p className={`text-3xl font-bold ${getProgressTextColor()}`}>{remainingCapacity}</p>
        </div>
      </div>
      {/* Visual Progress Bar */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Capacity Usage</span>
          <span className={`text-sm font-bold ${getProgressTextColor()}`}>
            {usagePercentage?.toFixed(1)}%
          </span>
        </div>
        
        <div className="w-full h-4 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${getProgressColor()}`}
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          />
        </div>

        {/* Usage Status Message */}
        <div className="flex items-start gap-2">
          {usagePercentage >= 90 ? (
            <>
              <Icon name="AlertTriangle" size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-500">
                <span className="font-semibold">At or near capacity limit.</span> Consider upgrading your plan to add more agents.
              </p>
            </>
          ) : usagePercentage >= 70 ? (
            <>
              <Icon name="AlertCircle" size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-500">
                <span className="font-semibold">Moderate usage.</span> You have {remainingCapacity} agent slot{remainingCapacity !== 1 ? 's' : ''} remaining.
              </p>
            </>
          ) : (
            <>
              <Icon name="CheckCircle" size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-500">
                <span className="font-semibold">Low usage.</span> You have plenty of capacity available.
              </p>
            </>
          )}
        </div>
      </div>
      {/* Agent Type Breakdown */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">Agent Type Breakdown</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {agentBreakdown?.map((agent, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg border ${
                agent?.isActive
                  ? 'bg-primary/5 border-primary/20' :'bg-muted/30 border-border'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-${agent?.color}-500/10`}>
                  <Icon name={agent?.icon} size={18} className={`text-${agent?.color}-500`} />
                </div>
                <span className={`text-sm font-medium ${
                  agent?.isActive ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {agent?.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {agent?.isActive ? (
                  <>
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-semibold text-emerald-500">Active</span>
                  </>
                ) : (
                  <span className="text-xs font-medium text-muted-foreground">Inactive</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AgentCapacityOverview;